import { Box, Grid, Button, useMediaQuery } from '@mui/material';
import { useState, useMemo, useEffect, useContext } from 'react';
import { useSnackbar } from 'notistack';
import {
  Card,
  SMBSelect,
  Table,
  MobileTable,
  Loader,
  Pagination,
  CSVDownloadButton
} from '../../../components';
import { getSalesBuilds } from '../../../fetch/lookups';
import { SALES_PORTAL_PER_PAGE_OPTIONS, formatDate, getEnvironment } from '../../../helpers';
import { IBuild, ISortDirection, IBuildRes, IDropdownResponse } from '../../../models';
import { PlanFinderFilters } from './plan-finder-filters';
import { getSalesLookupElevations, getSalesLookupPlans } from '../../../fetch/lookups/sales-portal';
import { SalesContext, update } from '../../../context/sales';

const PAGE_NAME = 'reports-plan-finder';
export const PlanFinder = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [selectedElevation, setSelectedElevation] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<string>('Territory');
  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodName?: ISortDirection;
    LotNumber?: ISortDirection;
    State?: ISortDirection;
    City?: ISortDirection;
    Territory?: ISortDirection;
    EstimatedCompletionDate?: ISortDirection;
    PlanDescription?: ISortDirection;
    Status?: ISortDirection;
  }>({
    Territory: 'Asc'
  });
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | null>('');
  const [selectedLotId, setSelectedLotId] = useState<string | null>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [filters, setFilters] = useState<{
    planId?: number | string;
    planElevationId?: number | string;
    neighborhoodId?: number | string;
    lotNumber?: number | string;
    status?:
      | 'New'
      | 'BuildApproved'
      | 'ContractAaccepted'
      | 'TenDayComplete'
      | 'Closing'
      | 'Complete'
      | string;
  } | null>({});
  const [builds, setBuilds] = useState<IBuild[] | null>(null);
  const [isLoadingBuilds, setIsLoadingBuilds] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );
  const [showResults, setShowResults] = useState<boolean>(false);

  const fetchBuilds = async () => {
    setIsLoadingBuilds(true);
    try {
      const res = await getSalesBuilds({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        isForPlanFinder: true,
        ...filters
      });
      setBuilds(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(`Error loading builds, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingBuilds(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Territory',
        accessor: 'territory',
        isServerSorted: selectedSort === 'Territory',
        isServerSortedDesc: sortDirection.Territory === 'Desc',
        handleClickColumn: () => handleClickColumn('Territory')
      },
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'planDescription',
        isServerSorted: selectedSort === 'PlanDescription',
        isServerSortedDesc: sortDirection.PlanDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('PlanDescription')
      },
      {
        Header: 'Status',
        accessor: 'status',
        isServerSorted: selectedSort === 'Status',
        isServerSortedDesc: sortDirection.Status === 'Desc',
        handleClickColumn: () => handleClickColumn('Status')
      },
      {
        Header: 'Est. Completion Date',
        accessor: 'estimatedCompletionDate',
        isServerSorted: selectedSort === 'EstimatedCompletionDate',
        isServerSortedDesc: sortDirection.EstimatedCompletionDate === 'Desc',
        handleClickColumn: () => handleClickColumn('EstimatedCompletionDate'),
        Cell: props => <>{formatDate(props.cell.row.original.estimatedCompletionDate)}</>
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builds, sortDirection, selectedSort]);

  useEffect(() => {
    if (selectedPlan && showResults) {
      fetchBuilds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    page,
    perPage,
    sortDirection,
    selectedSort,
    selectedPlan,
    selectedElevation,
    showResults
  ]);

  return (
    <>
      <Box mt={1}>
        <Card title="Options">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SMBSelect
                label="Plan"
                value={selectedPlan}
                handleClear={() => {
                  setSelectedPlan('');
                  setSelectedElevation('');
                  setShowResults(false);
                  setBuilds(null);
                }}
                shouldFetch
                onChange={e => {
                  setSelectedPlan(e.target.value);
                  setSelectedElevation('');
                  setSelectedNeighborhoodId('');
                  setSelectedLotId('');
                  setSelectedStatus('');
                  setShowResults(false);
                  setBuilds(null);
                }}
                apiRequest={() =>
                  getSalesLookupPlans({ showArchived: false, isForPlanFinder: true })
                }
                transformResponse={(res: IDropdownResponse[]) =>
                  res.map(r => ({
                    label: r.description,
                    value: `${r.value}`
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SMBSelect
                label="Elevation"
                value={selectedElevation}
                handleClear={() => {
                  setSelectedElevation('');
                  setShowResults(false);
                  setBuilds(null);
                }}
                shouldFetch={!!selectedPlan}
                refetch={[selectedPlan]}
                disabled={!selectedPlan}
                onChange={e => {
                  setSelectedElevation(e.target.value);
                  setShowResults(false);
                  setBuilds(null);
                }}
                apiRequest={() => getSalesLookupElevations(selectedPlan)}
                transformResponse={(res: IDropdownResponse[]) =>
                  res.map(r => ({
                    label: r.description,
                    value: `${r.value}`
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                disabled={!selectedPlan || isLoadingBuilds}
                color="secondary"
                variant="contained"
                type="button"
                onClick={() => {
                  setFilters({ planId: selectedPlan, planElevationId: selectedElevation });
                  setShowResults(true);
                }}
              >
                See Results
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Box>
      {isLoadingBuilds && !builds && (
        <Box mt={1}>
          <Loader position="centered" />
        </Box>
      )}
      {builds && (
        <>
          <PlanFinderFilters
            isLoading={isLoadingBuilds}
            selectedNeighborhoodId={selectedNeighborhoodId}
            selectedLotId={selectedLotId}
            setSelectedNeighborhoodId={setSelectedNeighborhoodId}
            setSelectedLotId={setSelectedLotId}
            setSelectedStatus={setSelectedStatus}
            selectedStatus={selectedStatus}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setPage(0);
                setFilters({
                  planId: selectedPlan,
                  planElevationId: selectedElevation,
                  neighborhoodId: '',
                  lotNumber: '',
                  status: ''
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  planId: selectedPlan || undefined,
                  planElevationId: selectedElevation || undefined,
                  neighborhoodId: selectedNeighborhoodId || undefined,
                  lotNumber: selectedLotId || undefined,
                  status: selectedStatus || undefined
                });
              }
            }}
          />
          <>
            <Box display="flex" justifyContent="flex-end">
              <CSVDownloadButton
                name="plan-finder-csv"
                fileNamePrefix="plan_finder_report"
                disabled={builds?.length === 0}
                columns={[
                  {
                    header: 'Territory',
                    key: 'territory'
                  },
                  { header: 'Neighborhood', key: 'neighborhood' },
                  { header: 'Lot', key: 'lotNumber' },
                  { header: 'Plan', key: 'planDescription' },
                  { header: 'Status', key: 'status' },
                  { header: 'Est. Completion Date', key: 'estimatedCompletionDate', type: 'date' }
                ]}
                apiRequest={() =>
                  getSalesBuilds({
                    perPage: '2147483647',
                    sortBy: selectedSort,
                    sortDirection: sortDirection[selectedSort] as string,
                    isForPlanFinder: true,
                    ...filters
                  })
                }
                transformResponse={(res: IBuildRes) => res?.records}
              >
                CSV
              </CSVDownloadButton>
            </Box>
            <Table
              stickyHeaders
              columns={columns}
              data={builds ?? []}
              isLoading={isLoadingBuilds}
              hidePagination
              mobileProps={{
                fields: columns
                  .filter(c => c.Header)
                  .map(c => ({
                    name: c.Header,
                    accessor: c.accessor,
                    Cell: c?.Cell
                  }))
              }}
              ResponsiveComponent={isMobile ? MobileTable : undefined}
            />
            {!isLoadingBuilds && builds?.length > 0 && (
              <Pagination
                page={page}
                count={recordCount}
                rowsPerPage={perPage}
                setPage={setPage}
                rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
                setRowsPerPage={itemsPerPage => {
                  return dispatchPerPage(
                    update({
                      pageName: PAGE_NAME,
                      environment: getEnvironment(),
                      itemsPerPage: itemsPerPage
                    })
                  );
                }}
              />
            )}
          </>
        </>
      )}
    </>
  );
};
